import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    AssetListVariant,
    getAssetColumns,
    getAssetActions,
    getAssetFilterItems,
    getAssetFilterToggles,
} from '../components/List/asset-list-config'
import {
    getAssets,
    getMyAssets,
    getSubmittedAssets,
    AssetListItem,
} from '../services'
import { getAssetDetailPath } from '../utils/navigation'
import { StatusPublished } from '../constants'

interface AssetListFilters {
    document_group: string
    department_id: string
    document_type: string
    status: string
    created_at: string[]
}

const VARIANT_CONFIG = {
    all: {
        queryKey: 'assets-list-all',
        filterDefaults: {
            document_group: '',
            department_id: '',
            document_type: '',
            created_at: [],
        } as Omit<AssetListFilters, 'status'>,
    },
    my: {
        queryKey: 'assets-list-my',
        filterDefaults: {
            document_group: '',
            department_id: '',
            document_type: '',
            status: StatusPublished,
            created_at: [],
        } as AssetListFilters,
    },
    pending: {
        queryKey: 'assets-submitted',
        filterDefaults: {
            document_group: '',
            department_id: '',
            document_type: '',
            created_at: [],
        } as Omit<AssetListFilters, 'status'>,
    },
} as const

export const useAssetList = (variant: AssetListVariant) => {
    const { t } = useTranslation('asset')
    const navigate = useNavigate()
    const config = VARIANT_CONFIG[variant]

    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<AssetListFilters>({
        defaults: { sort_by: 'created_at', sort_order: 'desc' },
        filterDefaults: config.filterDefaults as AssetListFilters,
    })

    const { control } = useForm<AssetListFilters>({
        defaultValues: filters,
    })

    // API call based on variant
    const fetchFn = useCallback(() => {
        switch (variant) {
            case 'all':
                return getAssets({ ...queryParams, status: StatusPublished })
            case 'my':
                return getMyAssets(queryParams)
            case 'pending':
                return getSubmittedAssets(queryParams)
        }
    }, [variant, queryParams])

    // Data fetching
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ASSETS, config.queryKey, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    // Navigation handler
    const handleView = useCallback(
        (row: AssetListItem) => navigate(getAssetDetailPath(row)),
        [navigate]
    )

    // Memoized configs
    const columns = useMemo(() => getAssetColumns(t, { variant }), [t, variant])

    const actions = useMemo(
        () => getAssetActions(t, handleView),
        [t, handleView]
    )

    const filterItems = useMemo(
        () => getAssetFilterItems(t, { showStatus: variant === 'my' }),
        [t, variant]
    )

    const filterToggles = useMemo(
        () => getAssetFilterToggles(t, variant),
        [t, variant]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        assets: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,
        searchInputProps,

        columns,
        actions,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        refetch,
        t,
    }
}
